import { Chip } from 'components/ui/chip'
import { ChevronRight } from './icons'
import { SYMPTOMS_FEEDBACK_META, type SymptomsFeedback } from '../../models/careBlockDetail'

interface Props {
  /** The member's stated goal, in their own words. */
  goal: string
  /** The visit the goal was captured in, shown as a stamp under it. */
  source?: { visit: string; date: string }
  /** Opens the goal in the Care Block drawer, where the care team leaves
   *  feedback on it the same way they do on a Care Guide block. */
  onOpen?: () => void
  /** The latest symptom answer left in the goal's drawer. Shown as a tag on the
   *  right of the stamp row; absent until someone submits one. */
  symptomStatus?: SymptomsFeedback
  /** Before the first visit the goal hasn't been uncovered yet, so the tab
   *  explains what will land here instead of showing an empty card. */
  hasHadFirstVisit: boolean
}

// Patient Overview → Uncovered Goals tab (id `care-continuity`, its name before
// the relabel). Holds the member's own words on what they most want to achieve
// with Ayble, uncovered during their first visit and surfaced unchanged to
// every provider who opens the chart. Clicking the card opens it like a Care
// Guide block — same activation and hover as `LaneBlockCard`.
export function CareContinuityPanel({ goal, source, onOpen, symptomStatus, hasHadFirstVisit }: Props) {
  if (!hasHadFirstVisit) {
    return (
      <p style={{
        margin: 0, padding: '16px 0 24px',
        fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', color: 'var(--blue-grey)',
        fontFamily: 'var(--font-body)',
      }}>
        This member's goal will appear here after their first visit is complete.
      </p>
    )
  }

  return (
    <div
      className={onOpen ? 'hover-shadow' : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? 'Open uncovered goal details' : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? (e: React.KeyboardEvent) => {
        // Space/Enter activate, matching a native button.
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() }
      } : undefined}
      style={{
        padding: '16px 20px',
        background: 'var(--canvas)', borderRadius: 8,
        cursor: onOpen ? 'pointer' : undefined,
      }}
    >
      <p style={{
        margin: 0,
        fontSize: 'var(--text-md-size)', lineHeight: 'var(--text-md-lh)', letterSpacing: '0.01em',
        color: 'var(--seafoam)', fontFamily: 'var(--font-body)',
      }}>
        {goal}
      </p>
      {(source || symptomStatus || onOpen) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          {source && (
            <p style={{
              flex: 1, minWidth: 0, margin: 0,
              fontSize: 'var(--text-xs-size)', lineHeight: 'var(--text-xs-lh)', fontWeight: 'var(--text-xs-weight)',
              color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
            }}>
              {source.visit} · {source.date}
            </p>
          )}
          {/* The drawer form's label and its unselected pill colours — the
              card reports the answer rather than grading it. */}
          {symptomStatus && (
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              <Chip variant="neutral">{SYMPTOMS_FEEDBACK_META[symptomStatus].formLabel}</Chip>
            </div>
          )}
          {/* Affordance that the card opens. */}
          {onOpen && (
            <span
              aria-hidden
              style={{
                marginLeft: symptomStatus ? 0 : 'auto', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, color: 'var(--blue-grey)',
              }}
            >
              <ChevronRight />
            </span>
          )}
        </div>
      )}
    </div>
  )
}
